import React, {useContext, useEffect, useLayoutEffect, useRef, useState, Suspense, lazy} from 'react';
import {BrowserRouter} from "react-router-dom";
import AppRouter from "./AppRouter";
import AnalyticsV2Bootstrap from "./components/analyticsV2/AnalyticsV2Bootstrap";
import NavBar from "./components/layout/header/NavBar/NavBar";
import Navigation from "./components/layout/header/Navigation/Navigation";
import {observer} from "mobx-react-lite";
import {Context} from "./index";
import {checkAuth} from "./http/userApi";
import Loading from "./components/commons/Loading/Loading";
import {fetchDirectChatSessions} from "./http/directChatApi";
import {
    fetchArea,
    fetchBedroom,
    fetchBuilding,
    fetchFloor,
    fetchHeating,
    fetchParking,
    fetchRepair,
    fetchRoom
} from "./http/paramsApi";
import MobileBottomNav from "./components/layout/MobileBottomNav/MobileBottomNav/MobileBottomNav";
import {getOwnerSessions} from "./http/chatApi";
import './styles/base/global.css'
import useSiteVersionSync from "./components/hooks/useSiteVersionSync/useSiteVersionSync";
import {syncMessagesAppBadge} from "./utils/chat/syncMessagesAppBadge";
import "./utils/pwa/pwaInstall";
import {fetchRequestChatUnread} from "./http/requestChatApi";

import {
    syncPushDevice,
    requestPushWorkerFlush
} from "./utils/push/push";

const CookieBannerManager = lazy(() => import("./utils/CookieBannerManager/CookieBannerManager"));
const ChatSocketBridge = lazy(() => import("./components/realtime/ChatSocketBridge/ChatSocketBridge"));

const YMInitializerLazy = lazy(() =>
    import("react-yandex-metrika").then((m) => {
        const Comp = m.YMInitializer || m.default?.YMInitializer || m.default;
        if (!Comp) {
            // чтобы не падало в рантайме
            return { default: () => null };
        }
        return { default: Comp };
    })
);

const AppContent = ({ navBarRef, navigationRef, cookiesAccepted, handleAcceptCookies, metrikaOn }) => {
    useSiteVersionSync();

    return (
        <>
            <div
                id="app-ready-marker"
                data-prerender-ready="true"
                style={{display: "none"}}
            />

            {metrikaOn && (
                <Suspense fallback={null}>
                    <YMInitializerLazy accounts={[99134839]} options={{webvisor: true}}/>
                </Suspense>
            )}

            <NavBar ref={navBarRef}/>
            <Navigation ref={navigationRef}/>

            <AppRouter/>
            <div data-prerender-remove="true">
                <MobileBottomNav/>
            </div>

            {!cookiesAccepted && (
                <div data-prerender-remove="true">
                    <Suspense fallback={null}>
                        <CookieBannerManager
                            cookiesAccepted={cookiesAccepted}
                            onAccept={handleAcceptCookies}
                        />
                    </Suspense>
                </div>
            )}
        </>
    );
};

const App = observer(() => {
    // <<<--- 2. Добавляем chat в контекст
    const {
        user,
        add,
        helper,
        apartmentChat,
        directChat,
        requestChat,
        messages
    } = useContext(Context);
    const [isLoading, setIsLoading] = useState(true);

    const navBarRef = useRef(null);
    const navigationRef = useRef(null);

    const totalUnreadCount = messages.totalUnreadCount;

    const [cookiesAccepted, setCookiesAccepted] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem("cookie_consent") === "true";
        }
        return false;
    });

    const [metrikaOn, setMetrikaOn] = useState(false);

    const fetchRequestUnread = async () => {
        try {
            const data = await fetchRequestChatUnread();

            requestChat.setReadState({
                unreadCount: data.unreadCount || 0,
                lastReadMessageId: data.lastReadMessageId || 0,
                lastReadAt: data.lastReadAt || null
            });
        } catch (e) {
            console.error("Ошибка загрузки счётчика чата заявок:", e?.message || e);
        }
    };

    useEffect(() => {
        syncMessagesAppBadge(totalUnreadCount);
    }, [totalUnreadCount]);

    useEffect(() => {
        if (!cookiesAccepted) {
            setMetrikaOn(false);
            return;
        }

        const enable = () => {
            // чуть позже, чтобы не мешать LCP/TTI
            setTimeout(() => setMetrikaOn(true), 2500);
        };

        if (document.readyState === "complete") {
            enable();
            return;
        }

        window.addEventListener("load", enable, { once: true });
        return () => window.removeEventListener("load", enable);
    }, [cookiesAccepted]);

    useEffect(() => {
        const handleAuthLogout = () => {
            user.setIsAuth(false);
            user.setUserData({});
        };

        window.addEventListener(
            'auth:logout',
            handleAuthLogout
        );

        return () => {
            window.removeEventListener(
                'auth:logout',
                handleAuthLogout
            );
        };
    }, [user]);

    const handleAcceptCookies = () => {
        setCookiesAccepted(true);
    }

    useEffect(() => {
        helper.setCookiesAccepted(cookiesAccepted);
    }, [cookiesAccepted, helper]);

    useEffect(() => {
        if (!("serviceWorker" in navigator)) return;

        const registerServiceWorker = () => {
            setTimeout(() => {
                navigator.serviceWorker
                    .register("/sw.js")
                    .then((reg) => console.log("✅ SW registered:", reg.scope))
                    .catch((err) => console.error("❌ SW register error:", err));
            }, 2000);
        };

        if (document.readyState === "complete") {
            registerServiceWorker();
            return;
        }

        window.addEventListener("load", registerServiceWorker, { once: true });

        return () => {
            window.removeEventListener("load", registerServiceWorker);
        };
    }, []);

    const initializeUser = async () => {
        try {
            const userData = await checkAuth();

            user.setIsAuth(true);
            user.setUserData(userData);

            return true;
        } catch (error) {
            const status = error.response?.status;

            /*
             * Полностью разлогиниваем только при
             * реальном отказе в авторизации.
             */
            if (
                status === 401 ||
                status === 403
            ) {
                user.setIsAuth(false);
                user.setUserData({});

                localStorage.removeItem('token');
            } else {
                console.warn(
                    'Не удалось проверить авторизацию:',
                    error.message
                );
            }

            return false;
        }
    };

    const fetchParams = async () => {
        try {
            const [
                bedrooms, buildings, floors, rooms,
                heatings, repairs, parkings, areas
            ] = await Promise.all([
                fetchBedroom(),
                fetchBuilding(),
                fetchFloor(),
                fetchRoom(),
                fetchHeating(),
                fetchRepair(),
                fetchParking(),
                fetchArea()
            ]);

            add.setBedrooms(bedrooms);
            add.setBuildings(buildings);
            add.setFloors(floors);
            add.setRooms(rooms);
            add.setHeatings(heatings);
            add.setRepairs(repairs);
            add.setParkings(parkings);
            add.setAreas(areas);

            add.setParamsLoaded(true);
            add.setParamsFromServer(true);
        } catch (error) {
            console.error("Ошибка загрузки параметров:", error);

            add.setParamsLoaded(true);
            add.setParamsFromServer(false);
        }
    };

    const fetchChats = async () => {
        apartmentChat.setIsLoading(true);

        try {
            const data = await getOwnerSessions();
            apartmentChat.setSessions(data.sessions || []);
            return data;
        } catch (e) {
            console.error(e.message);
            return null;
        } finally {
            apartmentChat.setIsLoading(false);
        }
    };

    const fetchDirectChats = async () => {
        try {
            const data = await fetchDirectChatSessions({
                page: 1,
                limit: 50
            });

            directChat.setSessions(data.sessions || []);
        } catch (e) {
            console.error("Ошибка загрузки личных чатов:", e.message);
        }
    };

    useEffect(() => {
        if (!user.isLoaded) return;

        let refreshPromise = null;

        const refreshMessagesFromServer = () => {
            if (refreshPromise) return refreshPromise;

            refreshPromise = (async () => {
                const tasks = [fetchChats()];

                if (user.isAuth) {
                    tasks.push(
                        fetchDirectChats(),
                        fetchRequestUnread()
                    );
                }

                await Promise.allSettled(tasks);
            })().finally(() => {
                refreshPromise = null;
            });

            return refreshPromise;
        };

        const handleGuestSessionReady = () => {
            // Новый guest chat появился уже после bootstrap приложения.
            // Сразу добавляем его в глобальный store, не ждём /chat.
            refreshMessagesFromServer();
        };

        const handleServiceWorkerMessage = (event) => {
            if (event?.data?.type !== "arento:push-received") return;

            // Push дошёл, значит серверное unread-состояние уже изменилось.
            // Перечитываем его, даже если Socket.IO event был пропущен.
            refreshMessagesFromServer();
        };

        window.addEventListener(
            "chat:guest-session-ready",
            handleGuestSessionReady
        );

        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.addEventListener(
                "message",
                handleServiceWorkerMessage
            );
        }

        return () => {
            window.removeEventListener(
                "chat:guest-session-ready",
                handleGuestSessionReady
            );

            if ("serviceWorker" in navigator) {
                navigator.serviceWorker.removeEventListener(
                    "message",
                    handleServiceWorkerMessage
                );
            }
        };
    }, [user.isLoaded, user.isAuth]);

    useEffect(() => {
        if (!user.isLoaded) {
            return;
        }

        const refreshMessagesState = async () => {
            // Invalidate requests belonging to the previous user/guest before
            // starting snapshots for this identity (also A -> B while isAuth=true).
            directChat.clearStore();
            requestChat.clearStore();
            apartmentChat.clearStore();
            if (user.isAuth) {
                await Promise.allSettled([
                    fetchChats(),
                    fetchDirectChats(),
                    fetchRequestUnread()
                ]);

                return;
            }

            /*
             * У гостя apartment chats тоже являются полноценными
             * сообщениями. Нельзя очищать весь MessagesStore только
             * потому, что user.isAuth === false.
             *
             * Direct/request chats требуют аккаунт — их очищаем.
             * Apartment chats всегда перечитываем по httpOnly sessionId.
             */
            await fetchChats();
        };

        refreshMessagesState();
    }, [user.isLoaded, user.isAuth, user.UserData?.id]);

    useEffect(() => {
        /*
         * Запускаем и после login, и после logout.
         *
         * После login функция использует JWT claim.
         * После logout — device credential reconcile.
         */
        syncPushDevice()
            .then(
                (
                    result
                ) => {
                    if (
                        !result?.ok &&
                        !result?.skipped
                    ) {
                        console.warn(
                            "Push device sync failed:",
                            result?.reason
                        );
                    }
                }
            )
            .catch(
                (
                    error
                ) => {
                    console.warn(
                        "Push device sync error:",
                        error
                    );
                }
            );

    }, [
        user.isAuth
    ]);

    useEffect(() => {
        const initializeApp = async () => {
            try {
                fetchParams();

                /*
                 * Сначала полностью определяем auth-состояние.
                 * После user.isLoaded=true отдельный effect выше
                 * гарантированно гидратирует message stores для
                 * пользователя ИЛИ гостя. Так нет гонки между
                 * clearAllRuntimeState() и гостевым fetchChats().
                 */
                await initializeUser();
            } catch (error) {
                console.error("Ошибка инициализации приложения:", error);
            } finally {
                user.setIsLoaded(true);
                setIsLoading(false);
            }
        };

        initializeApp();
    }, []);

    useEffect(() => {
        let foregroundRefreshPromise = null;
        let pushRecoveryPromise = null;
        let disposed = false;

        const refreshPushInBackground = () => {
            if (disposed || pushRecoveryPromise) return;

            // Preserve claim -> ACK flush, but never block message refresh on push.
            pushRecoveryPromise = (async () => {
                await syncPushDevice();
                if (!disposed) await requestPushWorkerFlush();
            })().catch(error => {
                console.warn("Push foreground recovery error:", error);
            }).finally(() => {
                pushRecoveryPromise = null;
            });
        };

        const refreshForegroundState = () => {
            if (disposed || !user.isLoaded || document.visibilityState !== 'visible') return;
            if (foregroundRefreshPromise) return foregroundRefreshPromise;

            foregroundRefreshPromise = (async () => {
                const isAuthed = await initializeUser();
                if (disposed) return;

                refreshPushInBackground();

                // Apartment chats are loaded for guests too, independently of SW.ready.
                const tasks = [fetchChats()];
                if (isAuthed) tasks.push(fetchDirectChats(), fetchRequestUnread());
                await Promise.allSettled(tasks);
            })().catch(error => {
                console.warn("Messages foreground refresh error:", error);
            }).finally(() => {
                foregroundRefreshPromise = null;
            });

            return foregroundRefreshPromise;
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') refreshForegroundState();
        };
        const handleFocus = () => refreshForegroundState();

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        return () => {
            disposed = true;
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    useEffect(() => {
        const handleOnline =
            async () => {
                try {
                    /*
                     * Сначала восстанавливаем/claim-им
                     * device credential.
                     */
                    await syncPushDevice();


                    /*
                     * И только после сохранения нового
                     * deviceSecret просим SW отправить
                     * durable ACK/click queue.
                     */
                    await requestPushWorkerFlush();

                } catch (
                    error
                    ) {
                    console.warn(
                        "Push online recovery error:",
                        error
                    );
                }
            };


        window.addEventListener(
            "online",
            handleOnline
        );


        return () => {
            window.removeEventListener(
                "online",
                handleOnline
            );
        };
    }, []);

    useEffect(() => {
        const fixYmapsLinks = () => {
            document
                .querySelectorAll('a[class*="copyright__logo"]')
                .forEach((link) => {
                    const href = link.getAttribute("href");

                    if (!href) {
                        link.setAttribute("href", "https://yandex.ru/maps/");
                    }

                    link.setAttribute("aria-label", "Яндекс Карты");
                    link.setAttribute("title", "Яндекс Карты");
                    link.setAttribute("rel", "noopener noreferrer");
                });
        };

        fixYmapsLinks();

        const observer = new MutationObserver(fixYmapsLinks);

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        return () => observer.disconnect();
    }, []);

    useLayoutEffect(() => {
        if (isLoading) return;

        const navEl = navBarRef.current;
        const nav2El = navigationRef.current;
        if (!navEl || !nav2El) return;

        const update = () => {
            helper.setNavBarHeight(navEl.getBoundingClientRect().height || 0);
            helper.setNavigationHeight(nav2El.getBoundingClientRect().height || 0);
        };

        update(); // сразу

        const ro = new ResizeObserver(update);
        ro.observe(navEl);
        ro.observe(nav2El);

        window.addEventListener("resize", update);

        return () => {
            ro.disconnect();
            window.removeEventListener("resize", update);
        };
    }, [isLoading, helper]);

    if (isLoading) {
        return (
            <div data-prerender-remove="true">
                <Loading/>
            </div>
        );
    }

    return (
        <BrowserRouter>
            <AnalyticsV2Bootstrap/>

            {isLoading ? (
                <div data-prerender-remove="true">
                    <Loading/>
                </div>
            ) : (
                <>
                    <div data-prerender-remove="true">
                        {user.isLoaded && (
                            <Suspense fallback={null}>
                                <ChatSocketBridge/>
                            </Suspense>
                        )}
                    </div>

                    <AppContent
                        navBarRef={navBarRef}
                        navigationRef={navigationRef}
                        cookiesAccepted={cookiesAccepted}
                        handleAcceptCookies={handleAcceptCookies}
                        metrikaOn={metrikaOn}
                    />
                </>
            )}
        </BrowserRouter>
    );
});

export default App;